// components/dashboard/OrderStatusChart.jsx
import React, { useState } from 'react';
import { Card, Row, Col, Progress, Tag, Empty, Skeleton, Segmented } from 'antd';
import {
  ClockCircleOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  PieChartOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Sector
} from 'recharts';
import CountUp from 'react-countup';

const COLORS = {
  NEW: '#2563eb',
  IN_PROGRESS: '#d97706',
  COMPLETED: '#059669',
  CANCELLED: '#dc2626',
  FAILED: '#94a3b8'
};

// Active shape for donut chart
const renderActiveShape = (props) => {
  const {
    cx, cy, innerRadius, outerRadius, startAngle, endAngle,
    fill, payload, value
  } = props;

  return (
    <g>
      <text x={cx} y={cy - 8} textAnchor="middle" fill="#262626" fontSize={22} fontWeight={600}>
        {value}
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle" fill="#94a3b8" fontSize={12}>
        {payload.title}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 6}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.15))' }}
      />
    </g>
  );
};

const OrderStatusChart = ({ data, loading = false }) => {
  const [viewMode, setViewMode] = useState('chart');
  const [activeIndex, setActiveIndex] = useState(0);

  const statusData = [
    {
      title: 'Yeni Siparisler',
      value: data?.statusBreakdown?.newOrders || 0,
      color: COLORS.NEW,
      icon: <ClockCircleOutlined />
    },
    {
      title: 'Islemde',
      value: data?.statusBreakdown?.inProgressOrders || 0,
      color: COLORS.IN_PROGRESS,
      icon: <SyncOutlined spin={!loading && data?.statusBreakdown?.inProgressOrders > 0} />
    },
    {
      title: 'Tamamlandi',
      value: data?.statusBreakdown?.completedOrders || 0,
      color: COLORS.COMPLETED,
      icon: <CheckCircleOutlined />
    },
    {
      title: 'Iptal Edildi',
      value: data?.statusBreakdown?.cancelledOrders || 0,
      color: COLORS.CANCELLED,
      icon: <CloseCircleOutlined />
    },
    {
      title: 'Basarisiz',
      value: data?.statusBreakdown?.failedOrders || 0,
      color: COLORS.FAILED,
      icon: <ExclamationCircleOutlined />
    }
  ];

  const total = data?.totalOrders || 0;
  const hasData = statusData.some(status => status.value > 0);

  // Loading skeleton
  if (loading) {
    return (
      <Card
        title={<Skeleton.Input active size="small" style={{ width: 180 }} />}
        style={{ height: '100%', borderRadius: 8 }}
      >
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Skeleton.Avatar active size={160} shape="circle" />
        </div>
      </Card>
    );
  }

  // Empty state
  if (!hasData) {
    return (
      <Card
        title="Siparis Durumu Dagilimi"
        style={{ height: '100%', borderRadius: 8 }}
      >
        <Empty description="Siparis verisi bulunamadi" />
      </Card>
    );
  }

  // Prepare chart data
  const chartData = statusData.filter(item => item.value > 0);

  // List view component
  const ListView = () => (
    <Row gutter={[12, 12]}>
      {statusData.map((status, index) => {
        const percentage = total > 0 ? (status.value / total * 100).toFixed(1) : 0;
        return (
          <Col key={index} xs={24} sm={12}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.08, duration: 0.3 }}
            >
              <Card
                size="small"
                className="card-hover-lift"
                style={{
                  borderLeft: `4px solid ${status.color}`,
                  borderRadius: 8
                }}
                bodyStyle={{ padding: '12px 16px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: status.color, fontWeight: 500, fontSize: 13 }}>
                    {status.icon}
                    <span style={{ marginLeft: 8 }}>{status.title}</span>
                  </span>
                  <Tag color={status.color} style={{ margin: 0, borderRadius: 10 }}>
                    {percentage}%
                  </Tag>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ color: status.color, fontSize: 24, fontWeight: 600 }}>
                    <CountUp end={status.value} duration={1} />
                  </span>
                  <span style={{ color: '#94a3b8', fontSize: 13 }}>/ {total}</span>
                </div>
                <div className="progress-animated">
                  <Progress
                    percent={parseFloat(percentage)}
                    strokeColor={status.color}
                    size="small"
                    showInfo={false}
                    style={{ marginTop: 8 }}
                  />
                </div>
              </Card>
            </motion.div>
          </Col>
        );
      })}
    </Row>
  );

  // Chart view component
  const ChartView = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
    >
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie
            activeIndex={activeIndex}
            activeShape={renderActiveShape}
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={55}
            outerRadius={80}
            paddingAngle={3}
            dataKey="value"
            onMouseEnter={(_, index) => setActiveIndex(index)}
            animationBegin={0}
            animationDuration={800}
            animationEasing="ease-out"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} style={{ cursor: 'pointer' }} />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const item = payload[0].payload;
                return (
                  <div style={{
                    backgroundColor: 'white',
                    padding: '8px 12px',
                    borderRadius: 6,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    border: `1px solid ${item.color}`
                  }}>
                    <div style={{ color: item.color, fontWeight: 500 }}>{item.title}</div>
                    <div>{item.value} siparis ({((item.value / total) * 100).toFixed(1)}%)</div>
                  </div>
                );
              }
              return null;
            }}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, marginTop: 8 }}>
        {chartData.map((item, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.05 }}
            onClick={() => setActiveIndex(index)}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '4px 10px',
              borderRadius: 12,
              backgroundColor: activeIndex === index ? item.color + '15' : '#f5f5f5',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <div style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: item.color,
              marginRight: 6
            }} />
            <span style={{ fontSize: 12, color: '#595959' }}>{item.title}</span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
    >
      <Card
        title="Siparis Durumu Dagilimi"
        style={{ height: '100%', borderRadius: 8 }}
        className="card-hover-lift"
        extra={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Tag color="blue" style={{ fontSize: 13, borderRadius: 10, margin: 0 }}>
              Toplam: {total}
            </Tag>
            <Segmented
              size="small"
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: 'chart', icon: <PieChartOutlined /> },
                { value: 'list', icon: <UnorderedListOutlined /> }
              ]}
            />
          </div>
        }
      >
        <AnimatePresence mode="wait">
          {viewMode === 'chart' ? (
            <motion.div
              key="chart"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChartView />
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <ListView />
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  );
};

export default OrderStatusChart;
